import type {
  CallbackLoggingParams,
  nullable,
  IHealth,
  IExtendedHealth,
  KpiUsageParams,
  KpiFailureParams,
  KpiSuccessParams,
  IKpiUsageOptionalProperties,
  IQosErrorOptionalProperties,
  KpiSuccessLoggingParams,
  KpiFailureLoggingParams,
  VetoFailureLoggingParams,
  FeatureUsageAndHealthConfig
} from "./generated/healthSmall.gen";
import { KpiType } from "./generated/healthSmall.gen";
import { FeatureUsageAndHealthLogger } from "./extensions/FeatureUsageAndHealthLogger";
import { TimeBasedEventBlocker } from "./extensions/TimeBasedEventBlocker";

/**
 * @see {IExtendedFeatureUsageAndHealthLogger}
 */
export class ExtendedHealth implements IExtendedHealth {
  /* Blockers that can prevent the emitting of events based on a timer. Used for preventing over-ingestion of events. */
  /* NOTE: We use separate blockers for each event type to prevent collisions for events with the same name. */
  private static kpiUsageBlocker = new TimeBasedEventBlocker<string>();
  private static kpiFailureBlocker = new TimeBasedEventBlocker<string>();
  private static kpiSuccessBlocker = new TimeBasedEventBlocker<string>();

  /**
   * Initializes the FeatureUsageAndHealthLogger with the provided configuration.
   *
   * @param healthLogger  Instance of Health 2.0 API to log KPI and QOS events -- e.g. `Health.instance`.
   * @param featureConfig  Configuration that specifies properties like feature's name, veto pillar, and so on, that are used by this class.
   */
  constructor(
    private readonly healthLogger: IHealth,
    private readonly featureConfig: FeatureUsageAndHealthConfig,
    private readonly kpiUsageEventBlocker?: TimeBasedEventBlocker<string>,
    private readonly kpiFailureEventBlocker?: TimeBasedEventBlocker<string>,
    private readonly kpiSuccessEventBlocker?: TimeBasedEventBlocker<string>
  ) {}

  private featureUsageAndHealthLogger = new FeatureUsageAndHealthLogger(
    this.healthLogger,
    this.featureConfig,
    this.kpiUsageEventBlocker ?? ExtendedHealth.kpiUsageBlocker,
    this.kpiSuccessEventBlocker ?? ExtendedHealth.kpiFailureBlocker,
    this.kpiFailureEventBlocker ?? ExtendedHealth.kpiSuccessBlocker
  );

  public static getInstance(
    healthLogger: IHealth,
    featureConfig: FeatureUsageAndHealthConfig,
    kpiUsageEventBlocker?: TimeBasedEventBlocker<string>,
    kpiFailureEventBlocker?: TimeBasedEventBlocker<string>,
    kpiSuccessEventBlocker?: TimeBasedEventBlocker<string>
  ): ExtendedHealth {
    return new ExtendedHealth(
      healthLogger,
      featureConfig,
      kpiUsageEventBlocker,
      kpiFailureEventBlocker,
      kpiSuccessEventBlocker
    );
  }

  /**
   * Logs a usage KPI.
   *
   * Automatically logs feature's all-up usage KPI and registers its corresponding veto pillar, if this hasn't been done before.
   *
   * @param kpiUsageParams Object with properties kpiName (name of the KPI), kpiType (type of KPI), optionalProperties (optional properties to log with KPI),
   * and blockFutureEmissionDurationInMs (optional duration for which to block future emissions of this event).
   *
   * @returns Whether the event was processed. An event is skipped if it was blocked in a previous call and the block duration has not expired.
   */
  public logKpiUsage(kpiUsageParams: KpiUsageParams | nullable): boolean {
    if (kpiUsageParams && kpiUsageParams.kpiName) {
      return this.featureUsageAndHealthLogger.logKpiUsage(
        kpiUsageParams.kpiName,
        kpiUsageParams.kpiType ?? KpiType.FailureBased,
        kpiUsageParams.optionalProperties,
        kpiUsageParams.blockFutureEmissionDurationInMs
      );
    }
    return false;
  }

  /**
   * Logs a failure for the provided usage KPI.
   *
   * Automatically logs feature's all-up usage KPI and registers its corresponding veto pillar, if this hasn't been done before.
   *
   * IMPORTANT: Unless optionalProperties.hasImpliedUsage is `true`, you must first log a failure-based KPI usage event with the same name
   * before logging this failure, or the event will be dropped by Health 2.0. Set this to `true` if you want to log a "standalone" error with no
   * prior KPI usage. See https://office.visualstudio.com/OC/_wiki/wikis/OC.wiki/24673/KPI-2.0-Implied-Usage-KPIs.
   *
   * @param kpiFailureParams Object with properties kpiName (name of the usage KPI associated with this error), errorName (name of the error),
   * optionalProperties (optional properties to log with KPI failure), optionalVeto (optional veto to log alongside KPI failure),
   * and blockFutureEmissionDurationInMs (optional duration for which to block future emissions of this event).
   *
   * @returns Whether the event was processed. An event is skipped if it was blocked in a previous call and the block duration has not expired.
   */
  public logKpiFailure(kpiFailureParams: KpiFailureParams | nullable): boolean {
    if (
      kpiFailureParams &&
      kpiFailureParams.kpiName &&
      kpiFailureParams.errorName
    ) {
      return this.featureUsageAndHealthLogger.logKpiFailure(
        kpiFailureParams.kpiName,
        kpiFailureParams.errorName,
        kpiFailureParams.optionalProperties,
        kpiFailureParams.optionalVeto,
        kpiFailureParams.blockFutureEmissionDurationInMs
      );
    }
    return false;
  }

  /**
   * Logs a success for the provided usage KPI.
   *
   * Automatically logs feature's all-up usage KPI and registers its corresponding veto pillar, if this hasn't been done before.
   *
   * IMPORTANT: Unless optionalProperties.hasImpliedUsage is `true`, you must first log a success-based KPI usage event with the same name
   * before logging this success, or the event will be dropped by Health 2.0. Set this to `true` if you want to log a "standalone" success with no
   * prior KPI usage. See https://office.visualstudio.com/OC/_wiki/wikis/OC.wiki/24673/KPI-2.0-Implied-Usage-KPIs.
   *
   * @param kpiSuccessParams Object with properties kpiName (name of the KPI), optionalProperties (optional properties to log with KPI),
   * and blockFutureEmissionDurationInMs (optional duration for which to block future emissions of this event).
   *
   * @returns Whether the event was processed. An event is skipped if it was blocked in a previous call and the block duration has not expired.
   */
  public logKpiSuccess(kpiSuccessParams: KpiSuccessParams | nullable): boolean {
    if (kpiSuccessParams && kpiSuccessParams.kpiName) {
      return this.featureUsageAndHealthLogger.logKpiSuccess(
        kpiSuccessParams.kpiName,
        kpiSuccessParams.optionalProperties,
        kpiSuccessParams.blockFutureEmissionDurationInMs
      );
    }
    return false;
  }

  /**
   * Ensures that this feature's top-level usage KPI (denominator) was logged and its veto pillar registered.
   *
   * IMPORTANT: You do NOT need to call this method in places already covered by events logged through this API, as it is automatically called
   * internally the first time any event (whether user action, activity, KPI, ...) is logged via this API. We expose this method publicly so
   * that consumers can register the denominator/pillar in code paths that are covered by existing telemetry that does **not** go through this
   * API (e.g. in shared code or common infrastructure). This eliminates the need for you to double log events (once in shared code, once in
   * Word Web code) simply to get the denominator/veto pillar registered in all paths.
   *
   * We register the denominator/veto pillar max once per day (not once per session because sessions can last several days and most dashboards,
   * including ASHA, use "daily" aggregations).
   *
   * @param optionalProperties Optional properties to log with usage KPI
   */
  public ensureFeatureDenominatorLoggedAndVetoPillarRegistered(
    optionalProperties: IKpiUsageOptionalProperties | nullable
  ): void {
    this.featureUsageAndHealthLogger.ensureFeatureDenominatorLoggedAndVetoPillarRegistered(
      optionalProperties
    );
  }

  /**
   * Helper function that surrounds the execution of a callback with logic to instrument:
   * - A KPI usage event before the callback is executed, and optionally:
   * - A KPI success event when the callback has been executed successfully, and/or optionally:
   * - A KPI failure and optional veto when the callback throws an exception during execution
   *
   * NOTE: The output and behavior of the callback is NOT changed by this function, and errors will still bubble up to the caller.
   *
   * @param callback  Callback to be executed with logging logic surrounding it.
   * @param loggingParams  Parameters to use when instrumenting logging events.
   */
  public executeCallbackWithLogging = (
    callback: (() => void) | nullable,
    loggingParams: CallbackLoggingParams | nullable
  ): void => {
    const kpiSuccessParams = "KpiSuccessLoggingParams";
    const kpiFailureParams = "KpiFailureLoggingParams";
    const vetoFailureParams = "VetoFailureLoggingParams";
    const isKpiSuccess = (
      kpiParamType: any
    ): kpiParamType is KpiSuccessLoggingParams =>
      kpiSuccessParams.includes(kpiParamType);
    const isKpiFailure = (
      kpiParamType: any
    ): kpiParamType is KpiFailureLoggingParams =>
      kpiFailureParams.includes(kpiParamType);
    const isVetoFailure = (
      kpiParamType: any
    ): kpiParamType is VetoFailureLoggingParams =>
      vetoFailureParams.includes(kpiParamType);

    let kpiToLog;
    let params;
    if (!!loggingParams?.kpiUsageLoggingParams) {
      kpiToLog = "Usage";
      params = loggingParams?.kpiUsageLoggingParams;
    } else if (!!loggingParams?.kpiSuccessLoggingParams) {
      kpiToLog = "UsageWithSuccess";
      params = loggingParams?.kpiSuccessLoggingParams;
    } else if (!!loggingParams?.kpiFailureLoggingParams) {
      kpiToLog = "UsageWithFailure";
      params = loggingParams?.kpiFailureLoggingParams;
    } else {
      kpiToLog = "UsageWithVetoFailure";
      params = loggingParams?.vetoFailureLoggingParams;
    }

    const usageName = params?.usageName;
    const usageKpiType = params?.usageKpiType;

    // Log a usage KPI
    this.logKpiUsage({
      kpiName: usageName,
      kpiType: usageKpiType ?? KpiType.FailureBased
    });

    try {
      // Execute the callback
      callback?.();

      // If a success config was provided, log a KPI success
      if (kpiToLog === "UsageWithSuccess" && isKpiSuccess(params)) {
        this.logKpiSuccess({
          kpiName: usageName,
          optionalProperties: params.successOptionalProperties
        });
      }
    } catch (error: any) {
      // If a failure config was provided, log a KPI failure
      if (
        (kpiToLog === "UsageWithFailure" && isKpiFailure(params)) ||
        (kpiToLog === "UsageWithVetoFailure" && isVetoFailure(params))
      ) {
        let vetoProperties:
          | ({ name: string } & IQosErrorOptionalProperties)
          | undefined;

        // If a veto config was provided, log a QOS failure as well
        if (isVetoFailure(params)) {
          vetoProperties = {
            name: params.vetoName ?? "",
            ...params.vetoOptionalProperties
          };
        }

        this.logKpiFailure({
          kpiName: params.usageName,
          errorName: params.failureErrorName,
          optionalProperties: params.failureOptionalProperties,
          optionalVeto: vetoProperties
        });
      }

      // IMPORTANT: We don't want to change the behavior of the callback, so after logging a failure we still bubble up the error to
      // the caller (so it can handle it or crash the app as appropriate)
      throw error;
    }
  };
}
